'use client';

import { ChangeEvent, useState } from 'react';
import { useEvent } from 'effector-react';
import { EffectorNext } from '@effector/next';
import { AvatarList } from '@nx-frontend/shared/ui/avatar';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { ContextMenuLayout, calculateContextMenuPosition } from '@nx-frontend/shared/ui/context-menu';
import { openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu/context-menu-store';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { UiInput, UiInputSize, UiInputView, UiIconSide } from '@nx-frontend/shared/ui/input';
import { ModalLayout } from '@nx-frontend/shared/ui/modal';
import { openModalEvent } from '@nx-frontend/shared/ui/modal/modal-store';
import { MultiSelect, MultiSelectItem, MultiSelectViewType } from '@nx-frontend/shared/ui/multi-select';
import { Radio } from '@nx-frontend/shared/ui/radio';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { UiTooltipPosition, UiTooltipTheme } from '@nx-frontend/shared/types';
import { ContextMenuItemData, ContextMenuItemType } from '@nx-frontend/shared/types';
import { IconKey } from '@nx-frontend/shared/types/icon';

interface CheckboxData {
  id: string;
  label: string;
  type: CheckboxType;
  disabled?: boolean;
  hasError?: boolean;
  boxSize?: CheckboxSize;
}

interface RadioData extends CheckboxData {
  value: string;
}

export function DemoComponent() {
  const [checkboxes, setCheckboxes] = useState<{
    [key: string]: boolean;
  }>({
    unchecked: false,
    checked: true,
    disabled: false,
    switchOn: true,
    switchOff: false,
    switchOnBig: true,
    switchOffBig: false,
    uncheckedDisabled: false,
    checkedDisabled: true,
    togglesmon: true,
    togglesmoff: false,
    errorSwitch: true,
    errorCheckbox: false,
    errorCheckboxActiv: true,
  });

  const openContextMenu = useEvent(openContextMenuEvent);
  const openModal = useEvent(openModalEvent);
  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    openContextMenu({ items: menuData, position: calculateContextMenuPosition(e.target, 4) });
  };

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [id]: checked,
    }));
  };

  const [selectedSize, setSelectedSize] = useState('Regular');

  const handleSizeChange = (newSize: string) => {
    setSelectedSize(newSize);
  };
  const mockAvatars = [
    {
      name: 'John Doe',
      bgClass: 'bg-red-500',
    },
    {
      name: 'Jane Smith',
      bgClass: 'bg-blue-500',
    },
    {
      name: 'Alex Johnson',
      bgClass: 'bg-green-500',
    },
    {
      name: 'Maria Garcia',
      bgClass: 'bg-yellow-500',
    },
    {
      name: 'Chris Lee',
      bgClass: 'bg-purple-500',
    },
    {
      name: 'Samantha Brown',
      bgClass: 'bg-orange-500',
    },
  ];
  const checkboxData: CheckboxData[] = [
    { id: 'unchecked', label: 'Unchecked', type: CheckboxType.CHECKBOX },
    {
      id: 'uncheckedDisabled',
      label: 'Unchecked Disabled',
      type: CheckboxType.CHECKBOX,
      disabled: true,
    },
    { id: 'checked', label: 'Checked', type: CheckboxType.CHECKBOX },
    {
      id: 'checkedDisabled',
      label: 'Checked Disabled',
      type: CheckboxType.CHECKBOX,
      disabled: true,
    },
    { id: 'switchOn', label: 'Switch On', type: CheckboxType.SWITCH, boxSize: CheckboxSize.MD },
    { id: 'switchOff', label: 'Switch Off', type: CheckboxType.SWITCH, boxSize: CheckboxSize.MD },
    {
      id: 'switchOnBig',
      label: 'Switch On Big',
      type: CheckboxType.SWITCH,
      boxSize: CheckboxSize.LG,
    },
    {
      id: 'switchOffBig',
      label: 'Switch Off Big',
      type: CheckboxType.SWITCH,
      boxSize: CheckboxSize.LG,
    },
    {
      id: 'togglesmon',
      label: 'Switch toggle sm on',
      type: CheckboxType.SWITCH,
      boxSize: CheckboxSize.SM,
    },
    {
      id: 'togglesmoff',
      label: 'Switch toggle sm off',
      type: CheckboxType.SWITCH,
      boxSize: CheckboxSize.SM,
    },
    {
      id: 'errorSwitch',
      label: 'Error Switch',
      type: CheckboxType.SWITCH,
      boxSize: CheckboxSize.LG,
      hasError: true,
    },
    {
      id: 'errorCheckbox',
      label: 'Error Checkbox Unchecked',
      type: CheckboxType.CHECKBOX,
      hasError: true,
    },
  ];
  const radioData: RadioData[] = [
    { id: 'regular', label: 'Regular', value: 'Regular', type: CheckboxType.CHECKBOX },
    {
      id: 'medium',
      label: 'Medium',
      value: 'Medium',
      type: CheckboxType.CHECKBOX,
    },
    {
      id: 'large',
      label: 'Large',
      value: 'Large',
      type: CheckboxType.CHECKBOX,
      hasError: true,
    },
    {
      id: 'radioDisabled',
      label: 'radioDisabled',
      value: 'radioDisabled',
      type: CheckboxType.CHECKBOX,
      disabled: true,
    },
  ];

  const mockItems: MultiSelectItem[] = [
    {
      name: 'Alice Johnson',
      checked: true,
      globalId: '1',
      icon: IconsMap.PERSON,
    },
    {
      name: 'Bob Smith',
      checked: true,
      globalId: '2',
    },
    {
      name: 'Charlie Davis',
      checked: false,
      globalId: '3',
    },
    {
      name: 'Alice Johnson',
      checked: false,
      globalId: '4',
    },
    {
      name: 'Bob Smith',
      checked: true,
      globalId: '5',
    },
    {
      name: 'Charlie Davis',
      checked: false,
      globalId: '6',
    },
  ];
  const [selectedIds, setSelectedIds] = useState<string>('');

  const handleChange = (ids: string) => {
    setSelectedIds(ids);
  };
  const renderedCheckboxes = checkboxData.map((data) => (
    <div key={data.id} className="flex items-center mr-4">
      <Checkbox
        id={data.id}
        label={data.label}
        checked={checkboxes[data.id]}
        onChange={(checked) => handleCheckboxChange(data.id, checked)}
        type={data.type}
        boxSize={data.boxSize}
        disabled={data.disabled}
        hasError={data.hasError}
      />
    </div>
  ));

  const renderedRadio = radioData.map((data) => {
    return (
      <div key={data.id} className="flex items-center mr-4">
        <Radio
          id={data.id}
          label={data.label}
          name="size"
          checked={selectedSize === data.value}
          onChange={() => handleSizeChange(data.value)}
          value={data.value}
          disabled={data.disabled}
          hasError={data.hasError}
        />
      </div>
    );
  });

  const menuData: ContextMenuItemData[] = [
    {
      type: ContextMenuItemType.HTML,
      html: (
        <>
          <Checkbox
            id="index"
            checked={true}
            type={CheckboxType.SWITCH}
            boxSize={CheckboxSize.SM}
            onChange={() => {
              return false;
            }}
          />
          <span>Custom HTML</span>
        </>
      ),
    },
    { type: ContextMenuItemType.DEFAULT, title: 'Upload files', icon: IconsMap.DOCUMENT },
    { type: ContextMenuItemType.DEFAULT, title: 'Rename', icon: IconsMap.DOCUMENT },
    {
      type: ContextMenuItemType.DEFAULT,
      title: 'Duplicate',
      icon: IconsMap.DOCUMENT,
    },
    {
      type: ContextMenuItemType.DEFAULT,
      title: 'Move to/Copy to',
      icon: IconsMap.FOLDER,
      subMenu: [
        { type: ContextMenuItemType.DEFAULT, title: 'Left', icon: IconsMap.FOLDER, isChecked: true },
        {
          type: ContextMenuItemType.DEFAULT,
          title: 'Right',
          icon: IconsMap.FOLDER,
        },
      ],
    },
    { type: ContextMenuItemType.DEFAULT, title: 'Open in new tab', icon: IconsMap.DOCUMENT, isSeparator: true },
    { type: ContextMenuItemType.DEFAULT, title: 'Share', icon: IconsMap.DOCUMENT },
    { type: ContextMenuItemType.DEFAULT, title: 'Copy link', icon: IconsMap.FOLDER },
    {
      type: ContextMenuItemType.DEFAULT,
      title: 'Delete',
      icon: IconsMap.DOCUMENT,
      isSeparator: true,
      subMenu: [
        { type: ContextMenuItemType.DEFAULT, title: 'Submenu Item 1', icon: IconsMap.DOCUMENT },
        { type: ContextMenuItemType.DEFAULT, title: 'Submenu Item 2', icon: IconsMap.DOCUMENT },
      ],
    },
    {
      type: ContextMenuItemType.DEFAULT,
      title: 'Manage members',
      icon: IconsMap.FOLDER,
      textStyleClasses: 'font-bold text-cyan-500',
    },
  ];

  const CubesInCorners = () => {
    return (
      <>
        <div
          className="absolute top-0 items-center justify-center left-0 w-2 h-2 text-white font-bold bg-blue-500 rounded-lg shadow-md p-6 m-6"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => handleContextMenu(e)}
        >
          Tl
        </div>
        <div
          className="absolute top-0 items-center justify-center right-0 w-2 h-2 text-white font-bold bg-red-500 rounded-lg shadow-md p-6 m-6"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => handleContextMenu(e)}
        >
          Tr
        </div>
        <div
          className="absolute bottom-0 items-center justify-center left-0 w-2 h-2 text-white font-bold bg-green-500 rounded-lg shadow-md p-6 m-6"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => handleContextMenu(e)}
        >
          Bl
        </div>
        <div
          className="absolute bottom-0 items-center justify-center right-0 w-2 h-2 text-white font-bold bg-yellow-500 rounded-lg shadow-md p-6 m-6"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => handleContextMenu(e)}
        >
          Br
        </div>
      </>
    );
  };
  const iconKeys = Object.keys(IconsMap);
  return (
    <>
      <div className="flex flex-col">{renderedCheckboxes}</div>
      <h3>Select Pizza Size == {selectedSize}</h3>
      <div className="flex flex-col">{renderedRadio}</div>
      <div className="flex gap-4 items-center justify-center p-4">
        <Button type={ButtonType.FLAT}>FLAT Button</Button>
        <Button type={ButtonType.FLAT} size={ButtonSize.MD}>
          Button MD
        </Button>
        <Button type={ButtonType.FLAT} size={ButtonSize.SM}>
          Button SM
        </Button>
        <Button type={ButtonType.FLAT} size={ButtonSize.XL} disabled>
          Disabled Button
        </Button>
      </div>
      <div className="flex gap-4 items-center justify-center p-4">
        <Button type={ButtonType.STROKED}>STROKED Button</Button>
        <Button type={ButtonType.STROKED} size={ButtonSize.MD}>
          Stroked Button MD
        </Button>
        <Button type={ButtonType.STROKED} size={ButtonSize.SM}>
          STROKED Button SM
        </Button>
        <Button type={ButtonType.STROKED} size={ButtonSize.XL} disabled>
          Disabled STROKED Button Xl
        </Button>
      </div>
      <div className="flex gap-4 items-center justify-center p-4">
        <Button type={ButtonType.BASIC}>BASIC Button</Button>
        <Button type={ButtonType.BASIC} size={ButtonSize.MD}>
          BASIC MD
        </Button>
        <Button type={ButtonType.BASIC} size={ButtonSize.SM}>
          BASIC SM
        </Button>
        <Button type={ButtonType.BASIC} size={ButtonSize.XL} disabled>
          Disabled BASIC Xl
        </Button>
      </div>
      <div className="flex gap-4 items-center justify-center p-4">
        <Button type={ButtonType.FLAT} isIcon>
          <Icon icon={IconsMap.DOCUMENT} />
        </Button>
        <Button type={ButtonType.FLAT} isIcon size={ButtonSize.MD}>
          <Icon icon={IconsMap.DOCUMENT} className="w-5" />
        </Button>
        <Button type={ButtonType.FLAT} isIcon size={ButtonSize.SM}>
          <Icon icon={IconsMap.DOCUMENT} className="w-4" />
        </Button>
        <Button type={ButtonType.STROKED} isIcon size={ButtonSize.XL}>
          <Icon icon={IconsMap.ARROW_DOWN} />
        </Button>
        <Button type={ButtonType.STROKED} isIcon size={ButtonSize.MD}>
          <Icon icon={IconsMap.ARROW_DOWN} className="w-5" />
        </Button>
        <Button type={ButtonType.STROKED} isIcon size={ButtonSize.SM}>
          <Icon icon={IconsMap.ARROW_DOWN} className="w-4" />
        </Button>

        <Button type={ButtonType.STROKED} isIcon size={ButtonSize.XL} disabled>
          <Icon icon={IconsMap.ARROW_DOWN} />
        </Button>
      </div>
      <button
        onClick={() => openModal()}
        className="bg-blue-300 text-blue-500 border border-blue-400 rounded-md px-5 py-2 hover:bg-blue-100"
      >
        Show Model PopUp
      </button>
      <CubesInCorners />
      <EffectorNext>
        <ModalLayout />
        <ContextMenuLayout />
      </EffectorNext>
      <Tooltip message="title" position={UiTooltipPosition.BOTTOM}>
        <Button type={ButtonType.FLAT} size={ButtonSize.XL}>
          Disabled BASIC XL
        </Button>
      </Tooltip>
      <Tooltip message="title" position={UiTooltipPosition.BOTTOM} theme={UiTooltipTheme.LIGHT}>
        <Button type={ButtonType.FLAT} size={ButtonSize.XL}>
          Disabled BASIC XL LiGHT
        </Button>
      </Tooltip>
      <div className="flex flex-wrap gap-4 p-8">
        {iconKeys.map((iconKey) => (
          <div key={iconKey} className="w-1/6 flex flex-col items-center justify-center">
            {IconsMap[iconKey as IconKey]} {iconKey}
          </div>
        ))}
      </div>
      <div className="items-center justify-center p-8">
        <div className="grid gap-6 mb-6 md:grid-cols-3">
          <UiInput
            id="Email1"
            type="text"
            label="Email1"
            value=""
            name="email"
            placeholder="Email"
            error={false}
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email2"
            type="text"
            label="Email2"
            value=""
            name="email"
            placeholder="Email"
            size={UiInputSize.LARGE}
            error={false}
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email3"
            type="text"
            label="Email3"
            value=""
            name="email"
            placeholder="Email"
            size={UiInputSize.SMALL}
            error={false}
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
        </div>
        <div className="grid gap-6 mb-6 md:grid-cols-3">
          <UiInput
            id="Email4"
            type="text"
            label="Email4"
            value=""
            name="email"
            placeholder="Email"
            error={false}
            disabled
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email5"
            type="text"
            label="Email5"
            value=""
            name="email"
            placeholder="Email"
            size={UiInputSize.LARGE}
            error={false}
            disabled
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email6"
            type="text"
            label="Email6"
            value=""
            name="email"
            placeholder="Email"
            size={UiInputSize.SMALL}
            error={false}
            disabled
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
        </div>
        <div className="grid gap-6 mb-6 md:grid-cols-3">
          <UiInput
            id="Email7"
            type="text"
            label="Email7"
            value=""
            name="email"
            placeholder="Email"
            view={UiInputView.LABEL_BORDER}
            icon={IconsMap.ESTIMATED_TIME}
            iconSide={UiIconSide.RIGHT}
            error
            errorText="Input filed can't be empty!"
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email8"
            type="text"
            label="Email8"
            value=""
            name="email"
            placeholder="Email"
            view={UiInputView.LABEL_TOP}
            icon={IconsMap.ESTIMATED_TIME}
            iconSide={UiIconSide.LEFT}
            size={UiInputSize.LARGE}
            error
            errorText="Input filed can't be empty!"
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email9"
            type="text"
            label="Email9"
            value=""
            name="email"
            placeholder="Email"
            view={UiInputView.LABEL_TOP}
            icon={IconsMap.ESTIMATED_TIME}
            iconSide={UiIconSide.RIGHT}
            size={UiInputSize.SMALL}
            error
            errorText="Input filed can't be empty!"
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
        </div>
        <div className="grid gap-6 mb-6 md:grid-cols-3">
          <UiInput
            id="Email10"
            type="text"
            label="Email7"
            value=""
            name="email"
            placeholder="Email"
            view={UiInputView.LABEL_BORDER}
            icon={IconsMap.ESTIMATED_TIME}
            iconSide={UiIconSide.RIGHT}
            errorText="Input filed can't be empty!"
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email11"
            type="text"
            label="Email8"
            value=""
            name="email"
            placeholder="Email"
            view={UiInputView.LABEL_BORDER}
            icon={IconsMap.ESTIMATED_TIME}
            iconSide={UiIconSide.LEFT}
            size={UiInputSize.LARGE}
            errorText="Input filed can't be empty!"
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
          <UiInput
            id="Email12"
            type="text"
            label="Email9"
            value=""
            name="email"
            placeholder="Email"
            view={UiInputView.LABEL_BORDER}
            icon={IconsMap.ESTIMATED_TIME}
            iconSide={UiIconSide.RIGHT}
            size={UiInputSize.SMALL}
            errorText="Input filed can't be empty!"
            onChange={function (e: ChangeEvent<HTMLInputElement>): void {
              throw new Error('Function not implemented.');
            }}
          />
        </div>
        <div className="flex items-center justify-center w-full">
          <div className="flex items-center w-[300px] justify-center border py-2">
            <MultiSelect
              title="All Owners"
              search={true}
              items={mockItems}
              viewType={MultiSelectViewType.AVATARS}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="flex items-center justify-center w-full">
          <div className="flex items-center w-[300px] justify-center border">
            <MultiSelect
              title="All Types"
              search={true}
              items={mockItems}
              viewType={MultiSelectViewType.DEFAULT}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="flex">
          <ul>{selectedIds}</ul>
        </div>
        <AvatarList avatars={mockAvatars} maxVisibleCount={3} />
      </div>
    </>
  );
}
